import { trackAnonymousEvent } from "./track-anonymous-event";
import { getOpenPanelClientForJungleWebApp } from "./openpanel-client";
import { removeAdClickIdsFromUrl } from "./openpanel-event-properties";

// react-helmet reports every head update, and a page can update its head several times; one page view per URL.
let lastReportedMarketingPageUrl: string | null = null;

type MarketingSitePageViewEvent = { pageViewEventName: string; pageViewEventProperties: Record<string, unknown> };

// Reports a deferred page view event with its fallback. Null when nothing is waiting.
let flushPendingPageViewEventWithFallback: (() => void) | null = null;

// Reports of other events of the page, held back until its deferred page view event has been reported.
let eventReportsWaitingForPageViewEvent: Array<() => void> = [];

/**
 * Reports a deferred page view event right now, as its fallback, if one is still waiting. Only for when
 * waiting is no longer possible (the page is being left, or another page view starts); an event of the
 * page itself should wait its turn with reportAfterPendingMarketingSitePageViewEvent instead.
 */
function flushPendingMarketingSitePageViewEvent(): void {
  flushPendingPageViewEventWithFallback?.();
}

/**
 * Runs `reportEvent` once the page's deferred view event is out; immediately when none is waiting. A page's
 * view must never come after an action taken on that page, and flushing the view as its fallback instead
 * would misreport every visitor who acts before the lookup answers. The wait is bounded by the lookup's own
 * timeout, and leaving the page releases everything at once.
 */
export function reportAfterPendingMarketingSitePageViewEvent(reportEvent: () => void): void {
  if (flushPendingPageViewEventWithFallback === null) {
    reportEvent();
    return;
  }
  eventReportsWaitingForPageViewEvent.push(reportEvent);
}

/**
 * The page URL a marketing `screen_view` records. An ad click lands with its click id (fbclid, gclid,
 * ttclid, …) on the URL; OpenPanel keeps the `screen_view` URL as `__path` on every later event of the
 * page-load and parses its query, so the click id is removed first (contract C9: never send fbclid to
 * OpenPanel). The utm_* parameters stay, since OpenPanel derives the session's campaign from them.
 */
function getMarketingPageUrlForAnalytics(): string {
  return removeAdClickIdsFromUrl(window.location.href);
}

/**
 * Page view for the public marketing site (jungleai.com: landing page, blog, pricing, …): OpenPanel's
 * built-in `screen_view`, then the page's own product event (e.g. "Marketing Page Viewed").
 *
 * The app keeps OpenPanel's automatic screen views off because its product events are an explicit
 * taxonomy. A marketing site is different: which page a visit started on, where it came from and which
 * campaign parameters were on the URL ARE the data. OpenPanel derives all three from the page URL of a
 * `screen_view`, and stamps that URL (`__path`) on every later event of the page-load.
 *
 * The order matters. OpenPanel fills in a session's entry page, referrer and campaign fields from the
 * FIRST event of the session, so the `screen_view` must go out before the product event; sent the other
 * way round, the session starts on an event with no URL and those fields stay empty.
 *
 * Call this from react-helmet's `onChangeClientState` on the Helmet that sets the page's `<title>`: the
 * SDK reads `document.title` at call time. Never call it for app pages: their URLs can carry deck ids
 * and one-time handoff parameters that do not belong in analytics.
 *
 * `shouldRecordScreenView` is false for the landing page's /testing design preview, which is not
 * marketing traffic but still reports which design was looked at.
 */
export function trackMarketingSitePageView({
  pageViewEventName,
  pageViewEventProperties,
  shouldRecordScreenView = true,
}: {
  pageViewEventName: string;
  pageViewEventProperties: Record<string, unknown>;
  shouldRecordScreenView?: boolean;
}): void {
  if (typeof window === "undefined") return;
  const pageUrl = getMarketingPageUrlForAnalytics();
  if (pageUrl === lastReportedMarketingPageUrl) return;
  flushPendingMarketingSitePageViewEvent();
  lastReportedMarketingPageUrl = pageUrl;

  if (shouldRecordScreenView) getOpenPanelClientForJungleWebApp().screenView(pageUrl);
  trackAnonymousEvent(pageViewEventName, pageViewEventProperties);
}

/**
 * Like trackMarketingSitePageView, for a page whose product event depends on something it must look up
 * first (the landing page asks whether the visitor already has an account).
 *
 * The `screen_view` still goes out immediately: it is what gives the session its entry page, referrer and
 * campaign, and delaying it would let a fast click become the session's first event. Only the product event
 * waits. It is reported exactly once: with the looked-up event, or with `pageViewEventIfNotResolvedInTime`
 * when the visitor leaves the page or another page view starts first. Other events of the page wait behind
 * it (reportAfterPendingMarketingSitePageViewEvent).
 */
export function trackMarketingSitePageViewWithDeferredEvent({
  deferredPageViewEvent,
  pageViewEventIfNotResolvedInTime,
  shouldRecordScreenView = true,
}: {
  deferredPageViewEvent: Promise<MarketingSitePageViewEvent>;
  pageViewEventIfNotResolvedInTime: MarketingSitePageViewEvent;
  shouldRecordScreenView?: boolean;
}): void {
  if (typeof window === "undefined") return;
  const pageUrl = getMarketingPageUrlForAnalytics();
  if (pageUrl === lastReportedMarketingPageUrl) return;
  flushPendingMarketingSitePageViewEvent();
  lastReportedMarketingPageUrl = pageUrl;

  if (shouldRecordScreenView) getOpenPanelClientForJungleWebApp().screenView(pageUrl);

  let hasReportedPageViewEvent = false;
  const reportPageViewEventOnce = (pageViewEvent: MarketingSitePageViewEvent) => {
    if (hasReportedPageViewEvent) return;
    hasReportedPageViewEvent = true;
    if (flushPendingPageViewEventWithFallback === reportFallback) flushPendingPageViewEventWithFallback = null;
    window.removeEventListener("pagehide", reportFallback);
    trackAnonymousEvent(pageViewEvent.pageViewEventName, pageViewEvent.pageViewEventProperties);

    const eventReportsToRelease = eventReportsWaitingForPageViewEvent;
    eventReportsWaitingForPageViewEvent = [];
    for (const reportEvent of eventReportsToRelease) reportEvent();
  };
  const reportFallback = () => reportPageViewEventOnce(pageViewEventIfNotResolvedInTime);
  flushPendingPageViewEventWithFallback = reportFallback;
  // A visitor who leaves before the lookup answers (a fast click on a link to the app) still gets a view,
  // and the click waiting behind it still gets reported.
  window.addEventListener("pagehide", reportFallback);
  void deferredPageViewEvent.then(reportPageViewEventOnce, reportFallback);
}

/** Test-only: forget the last reported URL so each test starts from a fresh page-load. */
export function resetMarketingSitePageViewForTests(): void {
  lastReportedMarketingPageUrl = null;
  flushPendingPageViewEventWithFallback = null;
  eventReportsWaitingForPageViewEvent = [];
}
